import { useEffect, useRef, useState, type DragEvent } from "react";
import {
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_VIDEO_EXTENSIONS,
  ALLOWED_VIDEO_MIME_TYPES,
  VIDEO_LIMITS,
  VIDEO_RECOMMENDATIONS,
  type StudyImageDto,
} from "@gazelab/shared";
import { api, errorMessage } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import { fmtBytes, fmtDate, fmtDuration } from "../../lib/format.js";
import { Badge, ConfirmDialog, ErrorAlert, Modal, Spinner } from "../../components/ui/index.js";
import { Icon } from "../../components/ui/Icon.js";
import { NoticeStrip } from "../../components/ui/Layout.js";
import { useStudy } from "./StudyLayout.js";
import { DEMO_LOCK_NOTE, useDemo } from "./AdminLayout.js";

const IMAGE_ACCEPT = ALLOWED_IMAGE_MIME_TYPES.join(",");
const VIDEO_ACCEPT = [...ALLOWED_VIDEO_MIME_TYPES, ...ALLOWED_VIDEO_EXTENSIONS].join(",");
/** Preferencia de este navegador: no volver a mostrar el aviso antes de subir videos. */
const SKIP_NOTICE_KEY = "gazelab:videos:skip-upload-notice";

function readSkipNotice(): boolean {
  try {
    return localStorage.getItem(SKIP_NOTICE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSkipNotice() {
  try {
    localStorage.setItem(SKIP_NOTICE_KEY, "1");
  } catch {
    // Sin almacenamiento disponible: el aviso se seguirá mostrando.
  }
}

const isImageFile = (f: File) => (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(f.type);
const isVideoFile = (f: File) =>
  (ALLOWED_VIDEO_MIME_TYPES as readonly string[]).includes(f.type) ||
  (ALLOWED_VIDEO_EXTENSIONS as readonly string[]).some((ext) => f.name.toLowerCase().endsWith(ext));

interface UploadItem {
  id: string;
  name: string;
  size: number;
  progress: number;
  state: "queued" | "uploading" | "done" | "error";
  error?: string;
}

/** Aviso con las características recomendadas de los videos (antes de elegirlos o soltarlos). */
function VideoRecommendationsDialog({ open, onContinue, onCancel }: { open: boolean; onContinue: (skipNext: boolean) => void; onCancel: () => void }) {
  const [skip, setSkip] = useState(false);
  return (
    <Modal
      open={open}
      title="Antes de subir tus videos"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={() => onContinue(skip)} data-testid="video-notice-continue">
            Continuar
          </button>
        </>
      }
    >
      <div data-testid="video-recommendations">
        <p>Para que el seguimiento de mirada sea confiable, recomendamos:</p>
        <dl className="mt-3 space-y-1.5">
          {VIDEO_RECOMMENDATIONS.map((r) => (
            <div key={r.label} className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-2">
              <dt className="font-semibold text-ink">{r.label}</dt>
              <dd>{r.text}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-xs text-slate-500">
          Los archivos fuera de lo recomendado se aceptan con una advertencia. Se rechazan solo los que no son video, duran menos de{" "}
          {VIDEO_LIMITS.minDurationMs / 1000} s o más de {VIDEO_LIMITS.maxDurationMs / 1000} s, o superan el tamaño máximo del servidor.
        </p>
        <label className="mt-3 inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} />
          No volver a mostrar en este navegador
        </label>
      </div>
    </Modal>
  );
}

/** Formatos aceptados y recomendaciones de ambos tipos, siempre a la vista. */
function AcceptedFormats({
  maxImageMb,
  maxImagePixels,
  maxVideoMb,
  ffmpegMissing,
  onImages,
  onVideos,
  onVideoDetails,
  disabled,
}: {
  maxImageMb: number | null;
  maxImagePixels: number | null;
  maxVideoMb: number | null;
  ffmpegMissing: boolean;
  onImages: () => void;
  onVideos: () => void;
  onVideoDetails: () => void;
  disabled: boolean;
}) {
  const L = VIDEO_LIMITS;
  return (
    <section className="grid gap-3 md:grid-cols-2" aria-label="Formatos aceptados" data-tour="media-formats">
      <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-800">
            <Icon name="photo" />
          </span>
          <h2 className="text-base font-bold text-ink">Imágenes</h2>
        </div>
        <ul className="mt-3 flex-1 space-y-1 text-sm text-slate-700">
          <li>
            <strong>Formatos:</strong> JPG, PNG o WebP (sin animación).
          </li>
          <li>
            <strong>Tamaño:</strong> hasta {maxImageMb ?? "…"} MB y {maxImagePixels ? `${Math.round(maxImagePixels / 1_000_000)} megapíxeles` : "…"} por archivo.
          </li>
          <li>
            <strong>Recomendado:</strong> 15–20 fotografías; cada una se muestra el tiempo configurado (5 s por defecto).
          </li>
        </ul>
        <button type="button" className="btn-primary mt-3 self-start" onClick={onImages} disabled={disabled} data-tour="media-upload" data-testid="image-upload">
          <Icon name="photo" className="h-4 w-4" /> Subir imágenes
        </button>
      </div>
      <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-tour="video-recs" data-testid="video-recommendations-strip">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-800">
            <Icon name="video" />
          </span>
          <h2 className="text-base font-bold text-ink">Videos</h2>
        </div>
        <ul className="mt-3 flex-1 space-y-1 text-sm text-slate-700">
          <li>
            <strong>Formatos:</strong> MP4 (H.264), WebM o MOV; se convierten solos a MP4 H.264, 30 fps.
          </li>
          <li>
            <strong>Duración:</strong> de {L.minDurationMs / 1000} a {L.maxDurationMs / 1000} s (recomendado {L.recommendedMinDurationMs / 1000}–{L.recommendedMaxDurationMs / 1000} s); se
            reproduce completo.
          </li>
          <li>
            <strong>Recomendado:</strong> {L.recommendedWidth} × {L.recommendedHeight}, 30 fps, hasta ~{L.recommendedMaxSizeMb} MB y {L.recommendedMaxCount} videos como máximo. Máximo del
            servidor: {maxVideoMb ?? "…"} MB por archivo.{" "}
            <button type="button" className="font-semibold text-sky-800 underline" onClick={onVideoDetails}>
              Ver todas
            </button>
          </li>
        </ul>
        {ffmpegMissing ? (
          <p className="mt-3 text-sm font-semibold text-amber-800">FFmpeg no está instalado en el servidor: no se pueden subir videos.</p>
        ) : (
          <button type="button" className="btn-primary mt-3 self-start" onClick={onVideos} disabled={disabled} data-testid="video-upload">
            <Icon name="video" className="h-4 w-4" /> Subir videos
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * Estímulos del estudio: imágenes y videos en un solo orden de presentación. Las imágenes se
 * suben juntas; los videos uno por uno (con avance) tras el aviso de características
 * recomendadas, y se convierten en segundo plano.
 */
export function StudyMediaPage() {
  const { study, reload: reloadStudy } = useStudy();
  const locked = useDemo()?.enabled === true;
  const media = useAsync(() => api.listImages(study.id), [study.id]);
  const system = useAsync(() => api.systemInfo(), []);
  const [error, setError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<StudyImageDto | null>(null);
  const [confirmHistory, setConfirmHistory] = useState<StudyImageDto | null>(null);
  const [preview, setPreview] = useState<StudyImageDto | null>(null);
  const [notice, setNotice] = useState<{ open: boolean; pending: File[] | null; details: boolean }>({ open: false, pending: null, details: false });
  const imageInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const list = media.data ?? [];
  const limits = system.data?.uploadLimits;
  const processing = list.some((v) => v.processingStatus === "processing");
  const uploading = uploadingImages || uploads.some((u) => u.state === "queued" || u.state === "uploading");

  // Mientras haya conversiones de video en curso, actualizar cada 3 s.
  const reloadRef = useRef(media.reload);
  reloadRef.current = media.reload;
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(() => void reloadRef.current(), 3000);
    return () => clearInterval(t);
  }, [processing]);
  const wasProcessing = useRef(false);
  useEffect(() => {
    if (wasProcessing.current && !processing) void reloadStudy();
    wasProcessing.current = processing;
  }, [processing, reloadStudy]);

  async function refresh() {
    await Promise.all([media.reload(), reloadStudy()]);
  }

  /** Sube los archivos según su tipo (las imágenes de inmediato; los videos tras el aviso). */
  function handleFiles(files: File[]) {
    setError(null);
    const images = files.filter(isImageFile);
    const videos = files.filter((f) => !isImageFile(f) && isVideoFile(f));
    const other = files.filter((f) => !isImageFile(f) && !isVideoFile(f));
    if (other.length) setError(`Formato no permitido (imágenes JPG, PNG o WebP; videos MP4, WebM o MOV): ${other.map((f) => f.name).join(", ")}`);
    if (images.length) void uploadImages(images);
    if (videos.length) {
      if (readSkipNotice()) void uploadVideos(videos);
      else setNotice({ open: true, pending: videos, details: false });
    }
  }

  function requestVideos() {
    if (readSkipNotice()) videoInput.current?.click();
    else setNotice({ open: true, pending: null, details: false });
  }

  async function uploadImages(files: File[]) {
    const maxBytes = (limits?.maxImageSizeMb ?? Infinity) * 1024 * 1024;
    const tooBig = files.filter((f) => f.size > maxBytes);
    if (tooBig.length) {
      setError(`Imágenes demasiado grandes (máximo ${limits?.maxImageSizeMb} MB): ${tooBig.map((f) => f.name).join(", ")}`);
      return;
    }
    setUploadingImages(true);
    try {
      await api.uploadImages(study.id, files);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUploadingImages(false);
    }
  }

  async function uploadVideos(files: File[]) {
    const maxMb = limits?.maxVideoSizeMb ?? null;
    const rejected: string[] = [];
    const accepted = files.filter((f) => {
      if (maxMb !== null && f.size > maxMb * 1024 * 1024) {
        rejected.push(`${f.name}: pesa ${fmtBytes(f.size)}; el máximo es ${maxMb} MB`);
        return false;
      }
      return true;
    });
    if (rejected.length) setError(rejected.join(" · "));
    if (!accepted.length) return;
    const items: UploadItem[] = accepted.map((f) => ({ id: crypto.randomUUID(), name: f.name, size: f.size, progress: 0, state: "queued" }));
    setUploads((u) => [...u.filter((x) => x.state !== "done"), ...items]);
    const patch = (id: string, p: Partial<UploadItem>) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, ...p } : x)));
    // Uno a la vez: cada video es una petición (límite de tamaño por archivo y avance claro).
    for (const [i, file] of accepted.entries()) {
      const item = items[i]!;
      patch(item.id, { state: "uploading" });
      try {
        await api.uploadVideo(study.id, file, (fraction) => patch(item.id, { progress: fraction }));
        patch(item.id, { state: "done", progress: 1 });
        await media.reload();
      } catch (e) {
        patch(item.id, { state: "error", error: errorMessage(e) });
      }
    }
    await refresh();
  }

  async function reorder(order: string[]) {
    setError(null);
    const prev = media.data;
    media.setData(order.map((id) => list.find((i) => i.id === id)!).map((v, idx) => ({ ...v, sortOrder: idx })));
    try {
      media.setData(await api.reorderImages(study.id, order));
    } catch (e) {
      media.setData(prev);
      setError(errorMessage(e));
    }
  }

  function move(index: number, delta: number) {
    const order = list.map((i) => i.id);
    const j = index + delta;
    if (j < 0 || j >= order.length) return;
    [order[index], order[j]] = [order[j]!, order[index]!];
    void reorder(order);
  }

  function onDropCard(targetId: string) {
    if (!dragId || dragId === targetId) return;
    const order = list.map((i) => i.id).filter((id) => id !== dragId);
    order.splice(order.indexOf(targetId), 0, dragId);
    setDragId(null);
    void reorder(order);
  }

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) handleFiles(Array.from(e.dataTransfer.files));
  }

  const ready = list.filter((v) => v.processingStatus === "ready" && v.isActive);
  const imageCount = list.filter((v) => v.kind === "image").length;
  const videoCount = list.length - imageCount;
  const videoMs = ready.reduce((acc, v) => acc + (v.durationMs ?? 0), 0);
  const totalBytes = ready.reduce((acc, v) => acc + v.sizeBytes, 0);
  const activeVideos = ready.filter((v) => v.kind === "video").length;
  const noun = (v: StudyImageDto) => (v.kind === "video" ? "video" : "imagen");
  const iconBtn =
    "inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:hover:bg-transparent";

  return (
    <div
      className={`space-y-4 rounded-2xl ${dragOver ? "outline-2 outline-dashed outline-offset-4 outline-sky-500" : ""}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
      }}
      onDrop={onDrop}
    >
      <div>
        <p className="text-sm text-slate-700">
          <strong className="text-ink">{imageCount}</strong> {imageCount === 1 ? "imagen" : "imágenes"} · <strong className="text-ink">{videoCount}</strong>{" "}
          {videoCount === 1 ? "video" : "videos"} · <strong className="text-ink">{ready.length}</strong> activos
          {activeVideos > 0 && (
            <>
              {" "}
              · videos: {fmtDuration(videoMs)} en total · {fmtBytes(totalBytes)} que descarga cada participante
            </>
          )}
        </p>
        <p className="text-xs text-slate-500">
          Un estudio puede combinar imágenes y videos; se presentan en el orden de las tarjetas. Arrástralas para reordenar o suelta archivos en cualquier parte de esta página.
          {study.status === "active" && " El estudio está activo: los cambios afectan solo a sesiones nuevas."}
        </p>
      </div>

      <AcceptedFormats
        maxImageMb={limits?.maxImageSizeMb ?? null}
        maxImagePixels={limits?.maxImagePixels ?? null}
        maxVideoMb={limits?.maxVideoSizeMb ?? null}
        ffmpegMissing={limits ? !limits.videoProcessingAvailable : false}
        onImages={() => imageInput.current?.click()}
        onVideos={requestVideos}
        onVideoDetails={() => setNotice({ open: true, pending: null, details: true })}
        disabled={uploading}
      />
      <input
        ref={imageInput}
        type="file"
        multiple
        accept={IMAGE_ACCEPT}
        className="sr-only"
        aria-label="Elegir imágenes para subir"
        data-testid="image-upload-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          handleFiles(files);
        }}
      />
      <input
        ref={videoInput}
        type="file"
        multiple
        accept={VIDEO_ACCEPT}
        className="sr-only"
        aria-label="Elegir videos para subir"
        data-testid="video-upload-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          void uploadVideos(files);
        }}
      />

      {activeVideos > 0 && !study.settings.pursuitValidation && (
        <NoticeStrip tone="info">
          El estudio tiene videos: se recomienda activar la <strong>validación con punto en movimiento</strong> (Configuración → Captura) para poder corregir la latencia en el análisis.
        </NoticeStrip>
      )}
      {activeVideos > VIDEO_LIMITS.recommendedMaxCount && (
        <NoticeStrip tone="warning" icon="alert">
          Hay {activeVideos} videos activos: más de {VIDEO_LIMITS.recommendedMaxCount} alarga la sesión y aumenta la pérdida de calibración.
        </NoticeStrip>
      )}

      {(uploads.length > 0 || uploadingImages) && (
        <ul className="space-y-1.5 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm" aria-label="Subidas" data-testid="upload-list">
          {uploadingImages && <li className="text-sm text-sky-800">Subiendo imágenes…</li>}
          {uploads.map((u) => (
            <li key={u.id} className="text-sm">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-medium" title={u.name}>
                  {u.name}
                </span>
                <span className="font-util text-xs text-slate-500">{fmtBytes(u.size)}</span>
                {u.state === "done" && <Badge tone="success">Subido</Badge>}
                {u.state === "error" && <Badge tone="danger">Error</Badge>}
                {u.state === "queued" && <Badge>En espera</Badge>}
                {u.state === "uploading" && <span className="font-util text-xs text-sky-800">{Math.round(u.progress * 100)} %</span>}
              </div>
              {u.state === "uploading" && (
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-sky-600 transition-[width]" style={{ width: `${Math.round(u.progress * 100)}%` }} />
                </div>
              )}
              {u.error && <p className="text-xs text-rose-700">{u.error}</p>}
            </li>
          ))}
          {!uploading && uploads.length > 0 && (
            <li>
              <button type="button" className="text-xs font-semibold text-sky-800 hover:underline" onClick={() => setUploads([])}>
                Limpiar lista
              </button>
            </li>
          )}
        </ul>
      )}

      <ErrorAlert error={error ?? media.error} />
      {media.loading && !media.data ? (
        <Spinner />
      ) : (
        <ol className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-3" aria-label="Estímulos en orden de presentación">
          {list.map((v, i) => {
            const isVideo = v.kind === "video";
            return (
              <li
                key={v.id}
                draggable
                onDragStart={() => setDragId(v.id)}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => dragId && e.preventDefault()}
                onDrop={(e) => {
                  if (dragId) {
                    e.preventDefault();
                    e.stopPropagation();
                    onDropCard(v.id);
                  }
                }}
                className={`flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition ${dragId === v.id ? "border-sky-500 ring-2 ring-sky-500" : "border-slate-200 hover:shadow-md"}`}
                data-testid={isVideo ? "video-card" : "image-row"}
                data-status={v.processingStatus}
                data-tour={i === 0 ? "media-card" : undefined}
              >
                <div className={`relative cursor-grab overflow-hidden active:cursor-grabbing ${isVideo ? "aspect-video bg-slate-900" : "aspect-[4/3] bg-slate-100"}`} title="Arrastra para reordenar">
                  {isVideo ? (
                    v.hasPoster && (
                      <img src={api.adminPosterUrl(study.id, v.id)} alt="" className={`absolute inset-0 h-full w-full object-contain ${v.isActive ? "" : "opacity-35 grayscale"}`} loading="lazy" />
                    )
                  ) : (
                    <img
                      src={api.adminImageUrl(study.id, v.id)}
                      alt={v.filenameOriginal}
                      className={`absolute inset-0 h-full w-full object-contain ${v.isActive ? "" : "opacity-35 grayscale"}`}
                      loading="lazy"
                    />
                  )}
                  {v.processingStatus === "processing" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-white/85">
                      <span className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden />
                      Convirtiendo…
                    </div>
                  )}
                  {v.processingStatus === "failed" && (
                    <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-rose-200">
                      <span>
                        <Icon name="alert" className="mx-auto mb-1 h-6 w-6" />
                        {v.processingError ?? "No se pudo convertir"}
                      </span>
                    </div>
                  )}
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-ink/80 px-1.5 py-0.5 font-util text-xs font-medium text-white">
                    {i + 1}
                    <Icon name={isVideo ? "video" : "photo"} className="h-3.5 w-3.5" />
                  </span>
                  {isVideo && v.processingStatus === "ready" && (
                    <>
                      <button
                        type="button"
                        className="absolute inset-0 m-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/85 text-ink opacity-0 shadow transition hover:opacity-100 focus:opacity-100"
                        onClick={() => setPreview(v)}
                        aria-label={`Ver ${v.filenameOriginal}`}
                      >
                        <Icon name="play" className="h-6 w-6" />
                      </button>
                      <span className="absolute bottom-2 right-2 rounded bg-ink/80 px-1.5 py-0.5 font-util text-xs text-white">{fmtDuration(v.durationMs)}</span>
                    </>
                  )}
                  {!isVideo && v.trialCount > 0 && (
                    <span className="absolute bottom-2 right-2 rounded-full bg-white/90 px-2 py-0.5 text-[0.7rem] font-medium text-slate-700 shadow-sm">{v.trialCount} ensayos</span>
                  )}
                  {!v.isActive && (
                    <span className="absolute right-2 top-2">
                      <Badge>{isVideo ? "Inactivo" : "Inactiva"}</Badge>
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-1 px-3 pt-2">
                  <p className="truncate text-sm font-semibold text-ink" title={v.filenameOriginal}>
                    {v.filenameOriginal}
                  </p>
                  <p className="text-xs text-slate-500" title={`Subido ${fmtDate(v.createdAt)} · ${v.mimeType} · SHA-256 ${v.sha256}`}>
                    {!isVideo ? (
                      `${v.naturalWidth}×${v.naturalHeight} px · ${fmtBytes(v.sizeBytes)}`
                    ) : v.processingStatus === "ready" ? (
                      <>
                        {v.naturalWidth}×{v.naturalHeight} · {v.fps ? `${Math.round(v.fps * 100) / 100} fps` : "—"} · {fmtBytes(v.sizeBytes)}
                        {v.hasAudio && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-slate-600" title="El video tiene pista de audio">
                            · <Icon name="volume" className="h-3.5 w-3.5" /> audio
                          </span>
                        )}
                      </>
                    ) : v.processingStatus === "processing" ? (
                      `Original: ${v.naturalWidth}×${v.naturalHeight} · ${fmtDuration(v.durationMs)} · ${fmtBytes(v.sizeBytes)}`
                    ) : (
                      "Elimínalo y vuelve a subirlo."
                    )}
                  </p>
                  {isVideo && v.trialCount > 0 && <p className="text-xs text-slate-500">{v.trialCount} ensayos guardados</p>}
                  {v.mediaWarnings.length > 0 && (
                    <ul className="space-y-0.5 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-900 ring-1 ring-inset ring-amber-200" data-testid="video-warnings">
                      {v.mediaWarnings.map((w) => (
                        <li key={w} className="flex gap-1">
                          <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
                          {w}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex items-center gap-0.5 px-2 pb-2 pt-1">
                  <button type="button" className={iconBtn} onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Subir ${v.filenameOriginal}`} title="Mover antes">
                    <Icon name="arrowLeft" className="h-4 w-4" />
                  </button>
                  <button type="button" className={iconBtn} onClick={() => move(i, 1)} disabled={i === list.length - 1} aria-label={`Bajar ${v.filenameOriginal}`} title="Mover después">
                    <Icon name="arrowRight" className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className={`${iconBtn} ml-auto w-auto gap-1 px-2.5 text-xs font-semibold`}
                    onClick={() => act(() => api.setImageActive(study.id, v.id, !v.isActive))}
                    disabled={v.processingStatus !== "ready"}
                    title={v.isActive ? "Excluir de las sesiones nuevas" : "Incluir en las sesiones nuevas"}
                  >
                    <Icon name={v.isActive ? "eyeOff" : "eye"} className="h-4 w-4" />
                    {v.isActive ? "Desactivar" : "Activar"}
                  </button>
                  <button
                    type="button"
                    className={`${iconBtn} hover:bg-rose-50 hover:text-rose-700`}
                    onClick={() => setToDelete(v)}
                    aria-label={`Eliminar ${v.filenameOriginal}`}
                    title={locked ? DEMO_LOCK_NOTE : "Eliminar"}
                    disabled={locked}
                  >
                    <Icon name="trash" className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
          <li>
            <div className="flex h-full min-h-[14rem] w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 p-4 text-center text-sm text-slate-500">
              <Icon name="plus" className="h-7 w-7" />
              <span className="font-semibold">{list.length === 0 ? "Agrega las primeras imágenes o videos" : "Agregar más"}</span>
              <div className="flex flex-wrap justify-center gap-2">
                <button type="button" className="btn-secondary px-3 py-1.5 text-xs" onClick={() => imageInput.current?.click()} disabled={uploading}>
                  <Icon name="photo" className="h-4 w-4" /> Imágenes
                </button>
                <button type="button" className="btn-secondary px-3 py-1.5 text-xs" onClick={requestVideos} disabled={uploading || (limits ? !limits.videoProcessingAvailable : false)}>
                  <Icon name="video" className="h-4 w-4" /> Videos
                </button>
              </div>
              <span className="text-xs">o arrastra los archivos aquí</span>
            </div>
          </li>
        </ol>
      )}

      <VideoRecommendationsDialog
        open={notice.open}
        onCancel={() => setNotice({ open: false, pending: null, details: false })}
        onContinue={(skipNext) => {
          if (skipNext) writeSkipNotice();
          const { pending, details } = notice;
          setNotice({ open: false, pending: null, details: false });
          if (pending) void uploadVideos(pending);
          else if (!details) videoInput.current?.click();
        }}
      />

      <Modal
        open={preview !== null}
        title={preview?.filenameOriginal ?? ""}
        onClose={() => setPreview(null)}
        footer={
          <button type="button" className="btn-secondary" onClick={() => setPreview(null)}>
            Cerrar
          </button>
        }
      >
        {preview && <video src={api.adminImageUrl(study.id, preview.id)} controls autoPlay muted={!preview.hasAudio} className="aspect-video w-full rounded-lg bg-black" data-testid="video-preview" />}
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title={`Eliminar ${toDelete ? noun(toDelete) : ""}`}
        danger
        message={
          toDelete && toDelete.trialCount > 0 ? (
            <p>
              «{toDelete.filenameOriginal}» tiene <strong>{toDelete.trialCount} ensayos guardados</strong>. Considera desactivarlo en lugar de eliminarlo para conservar los datos.
            </p>
          ) : (
            <p>Se eliminará «{toDelete?.filenameOriginal}» y sus ROIs.</p>
          )
        }
        confirmLabel={toDelete && toDelete.trialCount > 0 ? "Continuar…" : "Eliminar"}
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          const v = toDelete!;
          setToDelete(null);
          if (v.trialCount > 0) setConfirmHistory(v);
          else await act(() => api.deleteImage(study.id, v.id, false));
        }}
      />
      <ConfirmDialog
        open={confirmHistory !== null}
        title="Confirmar eliminación de datos históricos"
        danger
        requireText="ELIMINAR"
        message={
          <p>
            Esto borrará también los <strong>{confirmHistory?.trialCount} ensayos y todas sus muestras de mirada</strong> de este {confirmHistory ? noun(confirmHistory) : "estímulo"}, en
            todas las sesiones. No se puede deshacer.
          </p>
        }
        confirmLabel="Eliminar y borrar sus datos"
        onCancel={() => setConfirmHistory(null)}
        onConfirm={async () => {
          const v = confirmHistory!;
          setConfirmHistory(null);
          await act(() => api.deleteImage(study.id, v.id, true));
        }}
      />
    </div>
  );
}
